import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { countryMeta, languageMeta } from "@/lib/countries";
import { translate } from "@/lib/translations";

/**
 * Global Country + Language context.
 *
 * Source-of-truth priority:
 *   1. user.country / user.language (server-side, survives device swap)
 *   2. localStorage (for logged-out preview / public pages)
 *   3. Browser navigator.language → guess country/lang
 *   4. Fallback to US / English
 *
 * Persists changes back to the server when the user is logged in.
 */

const COUNTRY_KEY = "mlcn_country";
const LANG_KEY = "mlcn_language";

const CountryContext = createContext(null);

function detectFromBrowser() {
    if (typeof navigator === "undefined") return { country: "US", language: "en" };
    const tag = (navigator.language || "en-US").toLowerCase();
    // tag is like "en-us", "es-mx", "de-de", "fr"
    const [lang, region] = tag.split("-");
    const country = (region || (lang === "en" ? "us" : lang)).toUpperCase();
    return {
        country: countryMeta(country).code,
        language: languageMeta(lang).code,
    };
}

export function CountryProvider({ children }) {
    const { user } = useAuth();

    const [country, setCountryState] = useState(() => {
        return localStorage.getItem(COUNTRY_KEY) || detectFromBrowser().country;
    });
    const [language, setLanguageState] = useState(() => {
        return localStorage.getItem(LANG_KEY) || detectFromBrowser().language;
    });

    // Hydrate from server-stored user prefs the FIRST time the user record
    // becomes available. Once hydrated we never re-sync from the user
    // object — otherwise the auth-stored value will stomp on whatever the
    // user just picked in the header dropdown.
    const hydratedRef = React.useRef(false);
    useEffect(() => {
        if (!user || user === false) return;
        if (hydratedRef.current) return;
        if (user.country) {
            setCountryState(user.country);
            localStorage.setItem(COUNTRY_KEY, user.country);
        }
        if (user.language) {
            setLanguageState(user.language);
            localStorage.setItem(LANG_KEY, user.language);
        }
        hydratedRef.current = true;
    }, [user]);

    const persist = useCallback(async (patch) => {
        if (!user || user === false) return;
        try {
            await api.post("/me/country-language", patch);
        } catch (_e) { /* offline: localStorage still holds the value */ }
    }, [user]);

    const setCountry = useCallback((code) => {
        const c = countryMeta(code).code;
        setCountryState(c);
        localStorage.setItem(COUNTRY_KEY, c);
        persist({ country: c });
    }, [persist]);

    const setLanguage = useCallback((lang) => {
        const l = languageMeta(lang).code;
        setLanguageState(l);
        localStorage.setItem(LANG_KEY, l);
        persist({ language: l });
    }, [persist]);

    const t = useCallback((key, vars) => translate(language, key, vars), [language]);

    const value = useMemo(
        () => ({ country, language, setCountry, setLanguage, t, meta: countryMeta(country), langMeta: languageMeta(language) }),
        [country, language, setCountry, setLanguage, t]
    );

    return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
}

export const useCountry = () => {
    const ctx = useContext(CountryContext);
    if (!ctx) {
        // Safe fallback so components used outside the provider (e.g. landing
        // page before AuthProvider mounts) don't crash.
        return {
            country: "US",
            language: "en",
            setCountry: () => { },
            setLanguage: () => { },
            t: (k, vars) => translate("en", k, vars),
            meta: countryMeta("US"),
            langMeta: languageMeta("en"),
        };
    }
    return ctx;
};
